import { useState } from "react"
import { useParams, Link } from "wouter"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { Calendar, Car, Users, CreditCard, Wifi, DoorOpen, X, Check, Download, IdCard } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { FindMyBookingDialog } from "@/components/FindMyBookingDialog"
import { AIGuidePanel } from "@/components/AIGuidePanel"
import { Input } from "@/components/ui/input"
import { PaymentQR } from "@/components/PaymentQR"
import { api, ApiRequestError } from "@/lib/api"
import { formatCurrency, formatDateRange, formatDate, LOCALE_MAP } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { ReservationByToken } from "@fohet/caravan-booker-shared/types"
import { isCzOrSkIban, ibanToCzskAccount } from "@fohet/caravan-booker-shared/utils/iban"

const statusColors: Record<string, string> = {
    created: "bg-orange-50 border-orange-200 text-orange-700",
    paid: "bg-green-50 border-green-200 text-green-700",
    confirmed: "bg-blue-50 border-blue-200 text-blue-700",
    active: "bg-brand/10 border-brand/30 text-green-800",
    completed: "bg-slate-100 border-slate-200 text-slate-600",
    cancelled: "bg-red-50 border-red-200 text-red-700",
}

const accessCodeIcon: Record<string, React.ReactNode> = {
    gate: <DoorOpen className="size-4" />,
    wifi: <Wifi className="size-4" />,
}

export const ReservationDetailPage = () => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const { token } = useParams<{ token: string }>()
    const [showExtendModal, setShowExtendModal] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showRecoverDialog, setShowRecoverDialog] = useState(false)
    const queryClient = useQueryClient()

    const { data: reservation, isLoading, error } = useQuery({
        queryKey: ["reservation", "by-token", token],
        queryFn: () => api.get<ReservationByToken>(`/reservations/by-token/${token}`),
        retry: false,
    })

    const { register: registerExtend, handleSubmit: handleExtendSubmit, formState: { errors: extendErrors } } = useForm<{ newDepartureDate: string }>()

    const extendMutation = useMutation({
        mutationFn: (newDepartureDate: string) =>
            api.post("/reservations/extend", { token, newDepartureDate }),
        onSuccess: () => {
            toast.success(t("guest.detail.toastExtended"))
            setShowExtendModal(false)
            queryClient.invalidateQueries({ queryKey: ["reservation", "by-token", token] })
        },
        onError: (err) => {
            const code = err instanceof ApiRequestError ? err.code : undefined
            toast.error(code ? t(`errors.${code}`, { defaultValue: t("guest.detail.toastExtendFailed") }) : t("guest.detail.toastExtendFailed"))
        },
    })

    const bvcConsentMutation = useMutation({
        mutationFn: (consent: boolean) =>
            api.post<{ consent: boolean; sentNow: boolean }>(`/reservations/by-token/${token}/beskyd-card-consent`, { consent }),
        onSuccess: (data) => {
            if (data.consent) toast.success(t("guest.detail.bvc.optedIn"))
            queryClient.invalidateQueries({ queryKey: ["reservation", "by-token", token] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("guest.detail.bvc.failed"))
        },
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-slate-400 font-bold">{t("guest.detail.loading")}</div>
            </div>
        )
    }

    if (error || !reservation) {
        const isExpired = error instanceof ApiRequestError && error.code === "MAGIC_LINK_EXPIRED"
        return (
            <div className="max-w-md mx-auto space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 font-bold">
                    {isExpired ? t("guest.detail.expired") : t("guest.detail.notFound")}
                </div>
                <button
                    onClick={() => setShowRecoverDialog(true)}
                    className="w-full bg-slate-100 text-slate-600 font-bold rounded-[28px] py-3 px-6 hover:bg-slate-200 active:scale-95 transition-all"
                >
                    {t("guest.recover.link")}
                </button>
                <Link to="/" className="block text-center text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors">
                    {t("guest.recover.backToHome")}
                </Link>
                <FindMyBookingDialog open={showRecoverDialog} onClose={() => setShowRecoverDialog(false)} />
            </div>
        )
    }

    const isPaid = reservation.paymentStatus === "paid"
    const showAccessCodes = isPaid || (reservation.areal?.releaseCodesImmediately ?? false)
    const isCancelled = reservation.status === "cancelled"
    const currency = reservation.areal?.currency ?? "CZK"
    const canExtend = !isCancelled && reservation.status !== "completed"

    return (
        <div className="max-w-2xl mx-auto space-y-5 sm:pb-0 pb-28">
            {/* Header */}
            <div className="bg-charcoal text-white rounded-[40px] shadow-2xl p-5 sm:p-8">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">
                            {reservation.areal?.name ?? t("guest.detail.yourReservation")}
                        </p>
                        <h1 className="text-3xl font-black">{t("guest.detail.bookingDetails")}</h1>
                    </div>
                    <span className={cn(
                        "text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border",
                        statusColors[reservation.status] ?? "bg-slate-100 text-slate-600 border-slate-200",
                    )}>
                        {t(`common.status.${reservation.status}`, { defaultValue: reservation.status })}
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    <div className="flex items-center gap-2">
                        <Calendar className="size-4 text-slate-400" />
                        <div>
                            <p className="text-xs text-slate-400 font-bold">{t("guest.detail.labelDates")}</p>
                            <p className="font-black text-sm">{formatDateRange(reservation.arrivalDate, reservation.departureDate, locale)}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Users className="size-4 text-slate-400" />
                        <div>
                            <p className="text-xs text-slate-400 font-bold">{t("guest.detail.labelGuests")}</p>
                            <p className="font-black text-sm">{t("guest.detail.guestCount", { count: reservation.guestCount })}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Car className="size-4 text-slate-400" />
                        <div>
                            <p className="text-xs text-slate-400 font-bold">{t("guest.detail.labelVehicle")}</p>
                            <p className="font-black text-sm">{reservation.licensePlate}</p>
                        </div>
                    </div>
                    {reservation.spotNumber && (
                        <div className="flex items-center gap-2">
                            <span className="size-4 text-slate-400 font-black text-center">#</span>
                            <div>
                                <p className="text-xs text-slate-400 font-bold">{t("guest.detail.labelSpot")}</p>
                                <p className="font-black text-sm">{reservation.spotNumber}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-700 flex items-center justify-between">
                    <span className="text-slate-400 font-bold">{t("guest.detail.labelTotal")}</span>
                    <span className="text-2xl font-black text-brand">{formatCurrency(reservation.totalAmount, currency, locale)}</span>
                </div>
            </div>

            {/* Payment details */}
            {!isPaid && !isCancelled && (
                <div className="bg-orange-50 border border-orange-200 rounded-[40px] p-5 sm:p-8 space-y-3">
                    <div className="flex items-center gap-2">
                        <CreditCard className="size-5 text-orange-600" />
                        <h2 className="font-black text-orange-800">{t("guest.detail.paymentRequired")}</h2>
                    </div>
                    <p className="font-bold text-orange-700 text-sm">
                        {reservation.paymentMethod === "cash"
                            ? t("guest.detail.paymentInstructionCash", { amount: formatCurrency(reservation.totalAmount, currency, locale) })
                            : t("guest.detail.paymentInstruction", { amount: formatCurrency(reservation.totalAmount, currency, locale) })}
                    </p>
                    {reservation.areal?.bankAccount && reservation.paymentMethod !== "cash" && (
                        <div className="bg-white rounded-2xl p-4 space-y-2">
                            <div className="flex flex-wrap items-start justify-between gap-1">
                                <span className="text-xs font-black uppercase tracking-widest text-slate-500">{t("guest.detail.bankAccount")}</span>
                                <span className="font-black text-slate-900 font-mono text-sm break-all">{reservation.areal.bankAccount}</span>
                            </div>
                            {reservation.areal.bic && (
                                <div className="flex flex-wrap items-start justify-between gap-1">
                                    <span className="text-xs font-black uppercase tracking-widest text-slate-500">{t("guest.detail.bic")}</span>
                                    <span className="font-black text-slate-900 font-mono text-sm">{reservation.areal.bic}</span>
                                </div>
                            )}
                            {isCzOrSkIban(reservation.areal.bankAccount) && (() => {
                                const native = ibanToCzskAccount(reservation.areal.bankAccount!)
                                return native ? (
                                    <div className="flex flex-wrap items-start justify-between gap-1">
                                        <span className="text-xs font-black uppercase tracking-widest text-slate-500">{t("guest.detail.localAccountFormat")}</span>
                                        <span className="font-black text-slate-900">{native}</span>
                                    </div>
                                ) : null
                            })()}
                            {reservation.variableSymbol && (
                                <div className="flex flex-wrap items-start justify-between gap-1">
                                    <span className="text-xs font-black uppercase tracking-widest text-slate-500">{t("guest.detail.variableSymbol")}</span>
                                    <span className="font-black text-slate-900">{reservation.variableSymbol}</span>
                                </div>
                            )}
                            <div className="flex flex-wrap items-start justify-between gap-1">
                                <span className="text-xs font-black uppercase tracking-widest text-slate-500">{t("guest.detail.amount")}</span>
                                <span className="font-black text-slate-900">{formatCurrency(reservation.totalAmount, reservation.areal.currency, locale)}</span>
                            </div>
                        </div>
                    )}
                    {reservation.areal?.bankAccount &&
                        reservation.variableSymbol &&
                        (reservation.paymentMethod === "bank_transfer" ||
                            reservation.paymentMethod === "qr_payment") && (
                        <PaymentQR
                            bankAccount={reservation.areal.bankAccount}
                            variableSymbol={reservation.variableSymbol}
                            amountCents={reservation.totalAmount}
                            currency={reservation.areal.currency}
                            arealName={reservation.areal.name}
                        />
                    )}
                    {reservation.paymentDeadline && (
                        <p className="text-xs font-bold text-orange-600">
                            {t("guest.detail.payBy", { date: formatDate(reservation.paymentDeadline, locale) })}
                        </p>
                    )}
                </div>
            )}

            {/* Beskydy Card offer — shown after the QR/payment block. A single
                active checkbox = interest + consent to the program terms. */}
            {reservation.bvcOffer?.available && reservation.status !== "cancelled" && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8">
                    <div className="flex items-center gap-2 mb-3">
                        <IdCard className="size-5 text-brand" />
                        <h2 className="font-black text-slate-900">{t("guest.detail.bvc.title")}</h2>
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-4">{t("guest.detail.bvc.intro")}</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={reservation.bvcOffer.consent}
                            disabled={bvcConsentMutation.isPending}
                            onChange={(e) => bvcConsentMutation.mutate(e.target.checked)}
                            className="mt-0.5 size-5 rounded border-slate-300 text-brand focus:ring-brand disabled:opacity-40"
                        />
                        <span className="text-sm font-bold text-slate-700">{t("guest.detail.bvc.consentLabel")}</span>
                    </label>
                    <a
                        href="https://www.beskydycard.cz/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 ml-8 inline-block text-xs font-black text-brand underline hover:text-brand-dark"
                    >
                        {t("guest.detail.bvc.termsLink")}
                    </a>
                    {reservation.bvcOffer.consent && (
                        <p className="mt-3 text-xs font-black text-emerald-600">{t("guest.detail.bvc.confirmed")}</p>
                    )}
                </div>
            )}

            {/* Access codes */}
            {isPaid && reservation.accessCodes.length > 0 && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8">
                    <div className="flex items-center gap-2 mb-5">
                        <Check className="size-5 text-brand" />
                        <h2 className="font-black text-slate-900">{t("guest.detail.accessCodes")}</h2>
                    </div>
                    <div className="grid gap-3">
                        {reservation.accessCodes.map((code, i) => (
                            <div key={i} className="flex items-center justify-between bg-slate-50 rounded-2xl p-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-slate-500">{accessCodeIcon[code.type] ?? <DoorOpen className="size-4" />}</span>
                                    <div>
                                        <p className="text-xs font-black uppercase tracking-widest text-slate-500">{code.type}</p>
                                        {code.description && <p className="text-xs font-bold text-slate-400">{code.description}</p>}
                                    </div>
                                </div>
                                <span className="font-black text-slate-900 text-lg">{code.code}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Receipt / invoice download */}
            {(isPaid || reservation.paymentMethod === "bank_transfer" || reservation.paymentMethod === "qr_payment") && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="font-black text-slate-900">{t("guest.detail.taxReceipt")}</p>
                        <p className="text-xs font-bold text-slate-400">{t("guest.detail.taxReceiptDesc")}</p>
                    </div>
                    <a
                        href={`/api/reservations/by-token/${token}/receipt?lang=${i18n.language}`}
                        download
                        className="flex items-center gap-2 bg-brand text-white font-black rounded-[28px] px-5 py-3 shadow-md hover:bg-brand-dark transition-all active:scale-95 text-sm"
                    >
                        <Download className="size-4" />
                        {t("guest.detail.downloadReceipt")}
                    </a>
                </div>
            )}

            {/* Price breakdown */}
            {reservation.priceBreakdown && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8">
                    <h2 className="font-black text-slate-900 mb-4">{t("guest.detail.priceBreakdown")}</h2>
                    <div className="space-y-2">
                        <div className="flex justify-between font-bold text-sm text-slate-700">
                            <span>{t("guest.detail.accommodationLine", { count: reservation.nightCount })}</span>
                            <span>{formatCurrency(reservation.priceBreakdown.spot, currency, locale)}</span>
                        </div>
                        {reservation.priceBreakdown.taxes > 0 && (
                            <div className="flex justify-between font-bold text-sm text-slate-700">
                                <span>{t("guest.detail.localTax")}</span>
                                <span>{formatCurrency(reservation.priceBreakdown.taxes, currency, locale)}</span>
                            </div>
                        )}
                        {reservation.priceBreakdown.pet > 0 && (
                            <div className="flex justify-between font-bold text-sm text-slate-700">
                                <span>{t("guest.detail.petFee")}</span>
                                <span>{formatCurrency(reservation.priceBreakdown.pet, currency, locale)}</span>
                            </div>
                        )}
                        <div className="flex justify-between font-black text-slate-900 pt-2 border-t border-slate-100">
                            <span>{t("guest.detail.priceTotal")}</span>
                            <span>{formatCurrency(reservation.totalAmount, currency, locale)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Actions — desktop inline. px-5 py-3 keeps every button on the same
                baseline; varying the radius/weight here makes the row look ragged. */}
            {canExtend && (
                <div className="hidden sm:flex gap-3">
                    <Button type="button" onClick={() => setShowExtendModal(true)} className="flex-1 bg-brand text-white font-black rounded-[28px] px-5 py-3 shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200">
                        {t("guest.detail.extendStay")}
                    </Button>
                    <Button type="button" onClick={() => setShowCancelModal(true)} className="flex-1 bg-red-50 text-red-500 font-bold rounded-[28px] px-5 py-3 hover:bg-red-100 active:scale-95 transition-all duration-200">
                        {t("guest.detail.cancel")}
                    </Button>
                </div>
            )}

            {/* Camp navigation links — desktop inline */}
            {reservation.areal?.slug && (
                <div className="hidden sm:flex gap-3">
                    <a href={`/a/${reservation.areal.slug}`} className="flex-1 text-center bg-slate-100 text-slate-600 font-bold rounded-[28px] px-5 py-3 hover:bg-slate-200 active:scale-95 transition-all duration-200 text-sm">
                        {t("guest.detail.backToCamp")}
                    </a>
                    <a href={`/a/${reservation.areal.slug}/book`} className="flex-1 text-center bg-charcoal text-white font-black rounded-[28px] px-5 py-3 shadow-xl hover:opacity-90 active:scale-95 transition-all duration-200 text-sm">
                        {t("guest.detail.bookAgain")}
                    </a>
                </div>
            )}

            {/* Fixed bottom bar — mobile only */}
            <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-slate-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-20">
                <div className="max-w-2xl mx-auto grid grid-cols-2 gap-2">
                    {canExtend && (
                        <>
                            <Button type="button" onClick={() => setShowExtendModal(true)} className="bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-3 text-sm">
                                {t("guest.detail.extendStay")}
                            </Button>
                            <Button type="button" onClick={() => setShowCancelModal(true)} className="bg-red-50 text-red-500 font-bold rounded-xl hover:bg-red-100 active:scale-95 transition-all duration-200 py-3 text-sm">
                                {t("guest.detail.cancel")}
                            </Button>
                        </>
                    )}
                    {reservation.areal?.slug && (
                        <>
                            <a href={`/a/${reservation.areal.slug}`} className="text-center bg-slate-100 text-slate-600 font-bold rounded-2xl px-3 py-3 hover:bg-slate-200 active:scale-95 transition-all duration-200 text-sm flex items-center justify-center">
                                {t("guest.detail.backToCamp")}
                            </a>
                            <a href={`/a/${reservation.areal.slug}/book`} className="text-center bg-charcoal text-white font-black rounded-[28px] px-3 py-3 shadow-xl hover:opacity-90 active:scale-95 transition-all duration-200 text-sm flex items-center justify-center">
                                {t("guest.detail.bookAgain")}
                            </a>
                        </>
                    )}
                </div>
            </div>

            {/* Extend modal */}
            {showExtendModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-[40px] p-5 sm:p-8 w-full max-w-sm shadow-2xl">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-xl font-black">{t("guest.detail.extendModalTitle")}</h3>
                            <button onClick={() => setShowExtendModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="size-5" />
                            </button>
                        </div>
                        <form onSubmit={handleExtendSubmit((data) => extendMutation.mutate(data.newDepartureDate))} className="space-y-4">
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.detail.newDepartureDate")}
                                </label>
                                <Input
                                    type="date"
                                    min={reservation.departureDate}
                                    className="bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                                    error={extendErrors.newDepartureDate?.message}
                                    {...registerExtend("newDepartureDate", { required: t("components.guestForm.required") })}
                                />
                                <p className="text-xs font-bold text-slate-400 mt-1">
                                    {t("guest.detail.currentDeparture", { date: formatDate(reservation.departureDate, locale) })}
                                </p>
                            </div>
                            <Button
                                type="submit"
                                disabled={extendMutation.isPending}
                                className="w-full bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200"
                            >
                                {extendMutation.isPending ? t("guest.detail.extending") : t("guest.detail.confirmExtension")}
                            </Button>
                        </form>
                    </div>
                </div>
            )}

            {/* Cancel confirm modal */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-[40px] p-5 sm:p-8 w-full max-w-sm shadow-2xl">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-xl font-black text-red-600">{t("guest.detail.cancelModalTitle")}</h3>
                            <button onClick={() => setShowCancelModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="size-5" />
                            </button>
                        </div>
                        <p className="font-bold text-slate-600 mb-6">
                            {t("guest.detail.cancelConfirm")}
                        </p>
                        <div className="flex gap-3">
                            <Button
                                type="button"
                                onClick={() => setShowCancelModal(false)}
                                className="flex-1 bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200"
                            >
                                {t("guest.detail.keepBooking")}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => {
                                    // Cancel via PATCH with magic token — would need an endpoint; show contact info for now
                                    toast.info(t("guest.detail.toastCancelContact"))
                                    setShowCancelModal(false)
                                }}
                                className="flex-1 bg-red-50 text-red-500 font-bold rounded-xl hover:bg-red-100"
                            >
                                {t("guest.detail.cancelBooking")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {reservation.areal && (
                <AIGuidePanel arealId={reservation.arealId} arealName={reservation.areal.name} />
            )}
        </div>
    )
}
